import _ from "lodash";

export interface OaicCompletionSummary {
  total: number;
  completed: string[];
  incomplete: string[];
  completed_count: number;
  is_partial: boolean;
}

const normalizeOaicCompletionGroups = (value: any): string[] => {
  if (Array.isArray(value)) {
    return _.uniq(
      value
        .map((item) => String(item || "").trim())
        .filter(Boolean)
    );
  }

  if (_.isPlainObject(value)) {
    const objectValues = Object.values(value);
    const scalarValues = objectValues
      .filter(
        (item) => typeof item === "string" || typeof item === "number"
      )
      .map((item) => String(item || "").trim())
      .filter(Boolean);

    if (scalarValues.length > 0) {
      return _.uniq(scalarValues);
    }

    return _.uniq(
      Object.keys(value)
        .filter((key) => Boolean(value[key]))
        .map((key) => String(key || "").trim())
        .filter(Boolean)
    );
  }

  if (typeof value === "string" || typeof value === "number") {
    const normalized = String(value).trim();
    return normalized ? [normalized] : [];
  }

  return [];
};

export const getNormalizedOaicCompletion = (
  completion: any = {},
  legacySummary: any = {}
): OaicCompletionSummary => {
  // Older saved results predate completion metadata. Recover only from full
  // saved dimension profiles plus an actual score, never from overall alone.
  if (!_.isPlainObject(completion) || Object.keys(completion).length === 0) {
    const groups = ["dominant_characteristics", "organizational_leadership",
      "management_of_employees", "organization_glue", "strategic_emphases", "criteria_of_success"];
    const validProfile = (profile: any): boolean => {
      if (!_.isPlainObject(profile)) return false;
      const values = [profile.clan, profile.create, profile.complete ?? profile.compete, profile.control];
      return values.every(value => value !== null && value !== undefined && value !== "" &&
        Number.isFinite(Number(value)) && Number(value) >= 0 && Number(value) <= 100) &&
        Math.abs(values.reduce((sum, value) => sum + Number(value), 0) - 100) < 0.01;
    };
    const completed = groups.filter(group => {
      const score = legacySummary?.diff?.[group];
      return score !== null && score !== undefined && score !== "" && Number.isFinite(Number(score)) &&
        validProfile(legacySummary?.info?.candidate?.[group]) && validProfile(legacySummary?.info?.company?.[group]);
    });
    completion = { total: groups.length, completed, incomplete: groups.filter(group => !completed.includes(group)) };
  }
  const completed = normalizeOaicCompletionGroups(
    _.get(completion, "completed", [])
  );
  const incomplete = normalizeOaicCompletionGroups(
    _.get(completion, "incomplete", [])
  );
  const fallbackTotal = completed.length + incomplete.length;
  const total = Number(_.get(completion, "total", fallbackTotal)) || fallbackTotal;
  const completedCountValue = Number(
    _.get(completion, "completed_count", completed.length)
  );
  const completed_count = Number.isFinite(completedCountValue)
    ? completedCountValue
    : completed.length;

  return {
    total,
    completed,
    incomplete,
    completed_count,
    is_partial: Boolean(
      _.get(
        completion,
        "is_partial",
        completed_count > 0 && completed_count < total
      )
    ),
  };
};
